import type {
  GoogleMapsInstance,
  Hotel,
  LatLngBounds,
  MapCanvasProjection,
  OverlayView,
  Offer,
} from '@/modules/hotels/components/MapView/models';
import type {AnyProps, PointFeature} from 'supercluster';

import Supercluster from 'supercluster';

import {getRelativeRect} from '@/utils/geometry';

type SuperClusterPoint = PointFeature<AnyProps> & {
  id: string;
};

export const getElementBounds = ({
  mapCanvasProjection,
  element,
  container,
}: {
  mapCanvasProjection?: null | MapCanvasProjection;
  element?: null | HTMLElement;
  container?: null | HTMLElement;
}): LatLngBounds | null => {
  const rect = getRelativeRect(
    element?.getBoundingClientRect(),
    container?.getBoundingClientRect()
  );

  if (!mapCanvasProjection || !rect) {
    return null;
  }

  const southWestPoint = new window.google.maps.Point(rect.left, rect.bottom);
  const northEastPoint = new window.google.maps.Point(rect.right, rect.top);

  const southWestLatLng =
    mapCanvasProjection.fromContainerPixelToLatLng(southWestPoint);
  const northEastLatLng =
    mapCanvasProjection.fromContainerPixelToLatLng(northEastPoint);

  return new window.google.maps.LatLngBounds(southWestLatLng, northEastLatLng);
};

export const getLatLngBoundsForHotels = (
  hotels: Array<Hotel>
): LatLngBounds => {
  const bounds = new window.google.maps.LatLngBounds();

  for (const hotel of hotels) {
    const point = new window.google.maps.LatLng(hotel.lat, hotel.lon);

    bounds.extend(point);
  }

  return bounds;
};

/**
 * Creates a dummy overlay and adds it to the map so we can get access to the MapCanvasProjection.
 * Only this projection holds the methods to convert from container pixels to LatLng.
 *
 * @see https://stackoverflow.com/questions/1538681/how-to-call-fromlatlngtodivpixel-in-google-maps-api-v3
 */
export const createCanvasProjectionOverlay = (
  mapInstance: GoogleMapsInstance
): OverlayView => {
  // https://developers.google.com/maps/documentation/javascript/reference/overlay-view#OverlayView
  class CanvasProjectionOverlay extends google.maps.OverlayView {
    override onAdd() {
      // noop
    }
    override draw() {
      // noop
    }
    override onRemove() {
      // noop
    }
  }

  const canvasProjectionOverlay = new CanvasProjectionOverlay();
  canvasProjectionOverlay.setMap(mapInstance);

  return canvasProjectionOverlay;
};

export const hotelToGeoJsonFeature = (
  hotel: Hotel,
  index: number
): SuperClusterPoint => ({
  type: 'Feature',
  id: hotel.id,
  geometry: {
    type: 'Point',
    coordinates: [hotel.lon, hotel.lat],
  },
  properties: {
    index,
  },
});

export const hotelToOffer = (hotel: Hotel, travellerCount: number): Offer => ({
  totalPrice: hotel.totalPriceDisplay,
  totalPriceInternal: hotel.totalPrice,
  totalPriceOriginal: hotel.totalPriceOriginalDisplay,
  discountType: hotel.discountType,
  discountPercentage: hotel.discountPercentage,
  totalDiscountOriginal: hotel.totalDiscount,
  totalDiscountHint: hotel.totalDiscountHint,
  totalDiscountDisplay: hotel.totalDiscountDisplay,
  pricePerPerson: {
    amount: hotel.totalPriceDisplay.amount / travellerCount,
    currency: hotel.totalPriceDisplay.currency,
  },
  rating: hotel.rating,
});

export const clusterHotels = ({
  hotels,
  viewportBounds,
  zoom,
  limit,
  radius,
}: {
  hotels: Array<Hotel>;
  viewportBounds: LatLngBounds | null;
  zoom: number;
  limit: number;
  radius: number;
}) => {
  const points = hotels.map((hotel, index) =>
    hotelToGeoJsonFeature(hotel, index)
  );
  const bounds: [number, number, number, number] = [
    viewportBounds?.getSouthWest().lng() || 0,
    viewportBounds?.getSouthWest().lat() || 0,
    viewportBounds?.getNorthEast().lng() || 0,
    viewportBounds?.getNorthEast().lat() || 0,
  ];

  return uniformlyDistributePoints({
    points,
    bounds,
    zoom,
    limit,
    radius,
  });
};

const byIndex = (a: SuperClusterPoint, b: SuperClusterPoint) =>
  a.properties.index - b.properties.index;

const uniformlyDistributePoints = ({
  points,
  bounds,
  zoom,
  limit,
  radius,
}: {
  points: Array<SuperClusterPoint>;
  bounds: [
    number, // westLng
    number, // southLat
    number, // eastLng
    number, // northLat
  ];
  zoom: number;
  limit: number;
  radius: number;
}) => {
  const supercluster = new Supercluster({
    minZoom: zoom,
    maxZoom: zoom,
    radius,
  }).load(points);
  const clusters = supercluster.getClusters(
    bounds,
    zoom
  ) as Array<SuperClusterPoint>;
  let result: Array<SuperClusterPoint> = [];

  for (const clusterOrPoint of clusters) {
    if (clusterOrPoint.properties?.cluster === true) {
      const leaves = supercluster.getLeaves(
        clusterOrPoint.properties.cluster_id,
        Number.POSITIVE_INFINITY
      ) as Array<SuperClusterPoint>;
      leaves.sort(byIndex);
      result.push(leaves[0]);
    } else {
      result.push(clusterOrPoint);
    }
  }

  result.sort(byIndex);
  result = result.slice(0, limit);

  return result;
};
